import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Search } from 'lucide-react';
import {
  addEditor, fetchEditors, removeEditor, searchEditorCandidates,
} from './ProgramAdminApi';
import { Button, Card, CardTitle, Empty, Field, Input, Message } from './ui';
import { EmptyDay, MiniButton, PersonMeta } from './programAdminStyles';
import { formatDay } from './programAdminModel';

// The Editors tab (plan §6): who may work on this event's program, and the two actions that change
// it. Admins and the team reach it (authority.Permission PROGRAM_EDITORS_MANAGE); the grant mails
// the person, the removal is quiet (D8). Grants are per event, so this list is the event's own.
//
// Every confirmation is inline in the row it belongs to - the screen uses no window.confirm - and a
// refusal is shown in the backend's own words (the editor API answers plain text for 409s and the
// global ErrorDetails JSON for 404s).
const errorText = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === 'string' && data) return data;
  if (data && typeof data.message === 'string' && data.message) return data.message;
  return fallback;
};

// Long names and addresses are cut in the row and complete in the tooltip.
const ellipsis = {
  minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

const Row = ({ children }) => (
  <li style={{
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '.6rem',
    padding: '.6rem 0', borderTop: '1px solid #eef2f7', listStyle: 'none',
  }}>
    {children}
  </li>
);

const EditorsArea = ({ eventId }) => {
  const intl = useIntl();
  const t = useCallback((id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values), [intl]);

  const [editors, setEditors] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(null);
  // The add or remove that waits for its inline confirmation.
  const [confirmAdd, setConfirmAdd] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const searchSeq = useRef(0);

  const load = useCallback(async () => {
    try {
      setEditors((await fetchEditors(eventId)) || []);
    } catch (err) {
      setEditors([]);
      setError(errorText(err, t('programAdmin.editors.loadFailed', 'Could not load the editors.')));
    }
  }, [eventId, t]);

  useEffect(() => { load(); }, [load]);

  // At least two characters, after the same short pause as the other search boxes; a shorter query
  // asks nothing.
  useEffect(() => {
    const mine = ++searchSeq.current;
    const q = query.trim();
    if (q.length < 2) { setResults(null); setSearching(false); return undefined; }
    setSearching(true);
    const timer = setTimeout(() => {
      searchEditorCandidates(eventId, q)
        .then((rows) => { if (searchSeq.current === mine) setResults(rows || []); })
        .catch((err) => {
          if (searchSeq.current !== mine) return;
          setResults([]);
          setError(errorText(err, t('programAdmin.editors.searchFailed', 'The search failed.')));
        })
        .finally(() => { if (searchSeq.current === mine) setSearching(false); });
    }, 250);
    return () => clearTimeout(timer);
  }, [eventId, query, t]);

  const add = async (candidate) => {
    setBusy(`add:${candidate.accountId}`);
    setError(null);
    try {
      await addEditor(eventId, candidate.accountId);
      setConfirmAdd(null);
      setQuery('');
      setResults(null);
      await load();
    } catch (err) {
      setError(errorText(err, t('programAdmin.editors.addFailed', 'Could not add this person as an editor.')));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (personId) => {
    setBusy(`remove:${personId}`);
    setError(null);
    try {
      await removeEditor(eventId, personId);
      setConfirmRemove(null);
      await load();
    } catch (err) {
      setError(errorText(err, t('programAdmin.editors.removeFailed', 'Could not remove this editor.')));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardTitle>{t('programAdmin.editors.title', 'Program editors')}</CardTitle>
      <p style={{ margin: '0 0 1rem', fontSize: '.85rem', color: '#475569', lineHeight: 1.6 }}>
        {t('programAdmin.editors.intro',
          "Editors work on this event's program and its people, and see nothing else. They are e-mailed when you add them; removing them is quiet.")}
      </p>

      {error && <Message error role="alert">{error}</Message>}

      {editors === null ? (
        <Empty>{t('programAdmin.editors.loading', 'Loading editors…')}</Empty>
      ) : editors.length === 0 ? (
        <EmptyDay>{t('programAdmin.editors.empty', 'No editors yet.')}</EmptyDay>
      ) : (
        <ul style={{ margin: '0 0 1rem', padding: 0 }}>
          {editors.map((editor) => (
            <Row key={editor.personId}>
              <div style={{ flex: '1 1 14rem', minWidth: 0 }}>
                <div style={{ ...ellipsis, fontWeight: 700, color: '#111D4E' }} title={editor.name || undefined}>
                  {editor.name || '—'}
                </div>
                {editor.loginEmail && (
                  <div style={{ ...ellipsis, fontSize: '.78rem', color: '#64748b' }} title={editor.loginEmail}>
                    {editor.loginEmail}
                  </div>
                )}
                <div style={{ fontSize: '.76rem', color: '#64748b' }}>
                  {editor.grantedByName
                    ? t('programAdmin.editors.addedBy', 'Added by {name} on {date}', {
                      name: editor.grantedByName, date: formatDay(intl, editor.grantedAt),
                    })
                    : t('programAdmin.editors.addedOn', 'Added on {date}', { date: formatDay(intl, editor.grantedAt) })}
                </div>
              </div>

              {confirmRemove === editor.personId ? (
                <div role="alertdialog" aria-label={t('programAdmin.editors.removeAsk', 'Remove access to this program?')}
                  style={{ display: 'flex', flexDirection: 'column', gap: '.4rem', flex: '1 1 100%' }}>
                  <span style={{ fontSize: '.85rem', color: '#92400e' }}>
                    {t('programAdmin.editors.removeAsk', 'Remove access to this program?')}
                  </span>
                  <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
                    <Button type="button" danger disabled={busy === `remove:${editor.personId}`}
                      onClick={() => remove(editor.personId)}>
                      {t('programAdmin.editors.remove', 'Remove')}
                    </Button>
                    <Button type="button" secondary disabled={busy === `remove:${editor.personId}`}
                      onClick={() => setConfirmRemove(null)}>
                      {t('programAdmin.editors.keep', 'Keep')}
                    </Button>
                  </div>
                </div>
              ) : (
                <MiniButton type="button" disabled={busy !== null}
                  onClick={() => { setConfirmRemove(editor.personId); setConfirmAdd(null); }}>
                  {t('programAdmin.editors.remove', 'Remove')}
                </MiniButton>
              )}
            </Row>
          ))}
        </ul>
      )}

      <Field>
        {t('programAdmin.editors.searchLabel', 'Find an account by name or e-mail')}
        <div style={{ position: 'relative' }}>
          <Search size={14} aria-hidden="true"
            style={{ position: 'absolute', left: '.6rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <Input
            type="search"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setConfirmAdd(null); }}
            placeholder={t('programAdmin.editors.searchLabel', 'Find an account by name or e-mail')}
            aria-label={t('programAdmin.editors.searchLabel', 'Find an account by name or e-mail')}
            style={{ width: '100%', paddingLeft: '2rem' }}
          />
        </div>
      </Field>
      <p style={{ margin: '.4rem 0 .8rem', fontSize: '.78rem', color: '#64748b' }}>
        {t('programAdmin.editors.searchHint', 'Only people with an account can be added; anyone else signs up first.')}
      </p>

      {searching && <PersonMeta>{t('programAdmin.editors.searching', 'Searching…')}</PersonMeta>}
      {results !== null && results.length === 0 && !searching && (
        <Empty style={{ padding: '.5rem 0', textAlign: 'left' }}>
          {t('programAdmin.editors.noResults', 'No account found.')}
        </Empty>
      )}
      {results !== null && results.length > 0 && (
        <ul style={{ margin: 0, padding: 0 }}>
          {results.map((candidate) => (
            <Row key={candidate.accountId}>
              <div style={{ flex: '1 1 14rem', minWidth: 0 }}>
                <div style={{ ...ellipsis, fontWeight: 700, color: '#111D4E' }} title={candidate.name || undefined}>
                  {candidate.name || '—'}
                </div>
                {candidate.loginEmail && (
                  <div style={{ ...ellipsis, fontSize: '.78rem', color: '#64748b' }} title={candidate.loginEmail}>
                    {candidate.loginEmail}
                  </div>
                )}
              </div>

              {candidate.state === 'STAFF' && (
                <PersonMeta>{t('programAdmin.editors.staff', 'Admin or team - has access already')}</PersonMeta>
              )}
              {candidate.state === 'EDITOR' && (
                <PersonMeta>{t('programAdmin.editors.already', 'Already an editor')}</PersonMeta>
              )}
              {candidate.state === 'AVAILABLE' && (confirmAdd === candidate.accountId ? (
                <div role="alertdialog" aria-label={t('programAdmin.editors.addAskTitle', 'Add as editor?')}
                  style={{ display: 'flex', flexDirection: 'column', gap: '.4rem', flex: '1 1 100%' }}>
                  <span style={{ fontSize: '.85rem', color: '#334155' }}>
                    {t('programAdmin.editors.addAsk', '{name} gets an e-mail with the link to this program.', { name: candidate.name || candidate.loginEmail })}
                  </span>
                  <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
                    <Button type="button" disabled={busy === `add:${candidate.accountId}`} onClick={() => add(candidate)}>
                      {t('programAdmin.editors.addConfirm', 'Add and send e-mail')}
                    </Button>
                    <Button type="button" secondary disabled={busy === `add:${candidate.accountId}`}
                      onClick={() => setConfirmAdd(null)}>
                      {t('programAdmin.editors.cancel', 'Cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <MiniButton type="button" disabled={busy !== null}
                  onClick={() => { setConfirmAdd(candidate.accountId); setConfirmRemove(null); }}>
                  {t('programAdmin.editors.add', 'Add as editor')}
                </MiniButton>
              ))}
            </Row>
          ))}
        </ul>
      )}
    </Card>
  );
};

export default EditorsArea;
